/**
 * Course Builder Configuration
 * @description Configuration constants and enums for course builder functionality
 */

import React from "react";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import { PERMISSIONS } from "@constants/user.roles";

/** Organization type ID for PSDA Admin users who may see the Request tab (course approvals). */
export const COURSE_BUILDER_REQUEST_TAB_ORGANIZATION_TYPE_ID = 69;

export enum TabKeys {
	EQUIPMENTS = "equipments",
	LABS = "labs",
	COURSES = "courses",
	REQUEST = "request",
}

export const TAB_ROUTES = {
	[TabKeys.EQUIPMENTS]: BROWSER_ROUTES.COURSE_BUILDER_EQUIPMENT,
	[TabKeys.LABS]: BROWSER_ROUTES.COURSE_BUILDER_LABS,
	[TabKeys.COURSES]: BROWSER_ROUTES.COURSE_BUILDER_COURSES,
	[TabKeys.REQUEST]: BROWSER_ROUTES.COURSE_BUILDER_COURSES, // Request shares courses route context
} as const;

export enum TabLabels {
	EQUIPMENTS = "Equipments",
	LABS = "Labs",
	COURSES = "Courses",
	REQUEST = "Request",
}

export const TabIcons = {
	EQUIPMENTS: ConstructionOutlinedIcon,
	LABS: ScienceOutlinedIcon,
	COURSES: SchoolOutlinedIcon,
	REQUEST: AssignmentOutlinedIcon,
} as const;

/** Primary color for Course Builder section headers — matches active tab chrome. */
export const courseBuilderSectionIconProps = {
	color: "primary",
} satisfies SvgIconProps;

/** Shared card icon chrome — Equipments & Labs cards. */
export const courseBuilderCardIconWrapClassName = "p-2 bg-green-100 rounded-full";
export const courseBuilderCardIconClassName = "text-green-600 text-xl";
export const courseBuilderCardHoverBorderClassName = "hover:border-green-400";

export const DEFAULT_ACTIVE_TAB = TabKeys.EQUIPMENTS;

// Note: Disabled tabs are now calculated dynamically based on equipment and lab availability
// This is handled in TabNavigationContainer.tsx
export const DEFAULT_DISABLED_TABS = [TabKeys.LABS, TabKeys.COURSES]; // Both labs and courses are disabled by default

export const TAB_CONFIG = {
	[TabKeys.EQUIPMENTS]: {
		key: TabKeys.EQUIPMENTS,
		label: TabLabels.EQUIPMENTS,
		icon: React.createElement(TabIcons.EQUIPMENTS),
		requiredPermission: PERMISSIONS.EQUIPMENTS_VIEW,
	},
	[TabKeys.LABS]: {
		key: TabKeys.LABS,
		label: TabLabels.LABS,
		icon: React.createElement(TabIcons.LABS),
		requiredPermission: PERMISSIONS.LABS_VIEW,
	},
	[TabKeys.COURSES]: {
		key: TabKeys.COURSES,
		label: TabLabels.COURSES,
		icon: React.createElement(TabIcons.COURSES),
		requiredPermission: PERMISSIONS.COURSES_VIEW,
	},
	[TabKeys.REQUEST]: {
		key: TabKeys.REQUEST,
		label: TabLabels.REQUEST,
		icon: React.createElement(TabIcons.REQUEST),
		requiredPermission: PERMISSIONS.COURSES_LIST,
	},
} as const;

export const getTabConfig = () => Object.values(TAB_CONFIG);

export const getDefaultTabs = () => [
	TAB_CONFIG[TabKeys.EQUIPMENTS],
	TAB_CONFIG[TabKeys.LABS],
	TAB_CONFIG[TabKeys.COURSES],
	TAB_CONFIG[TabKeys.REQUEST],
];
